// client/src/components/pages/RouteSummary/ReconView.tsx (converted from .js, ST-00216)
//
// Recon view: every ticket of the summary's route(s) and period, ordered by Reg
// No and Ticket Timestamp, with column filters, calculated columns, find, the
// nett-weight sum and (admins) bulk-open in Amendments.
//
// ST-00218 (lm-status): with load matching on, a "Load status" column (each
// ticket's label from the tenant's load-match ledger) follows Ticket No, in the
// column picker and in Export CSV. The Recon list has no pairing column of its
// own, so nothing is replaced. It has no column filter (the server cannot filter
// on it) and is not offered to calculated-column formulas.
import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useContext,
  type ComponentType,
  type Context,
  type ForwardRefExoticComponent,
  type MouseEvent,
  type ReactNode,
  type RefAttributes,
} from 'react';
import {
  FaSpinner,
  FaFileExport,
  FaChevronLeft,
  FaChevronRight,
  FaFilter,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
  FaSearch,
  FaCalculator,
  FaEye,
  FaEyeSlash,
  FaCheckSquare,
  FaExternalLinkAlt,
  FaShareAlt,
} from 'react-icons/fa';
import api from '../../../services/api';
import { formatMiningDate, formatMiningDateTime } from '../../../utils/dateUtils';
import formulaEvaluatorJs from '../../../utils/formulaEvaluator';
import CalculatedColumnModalJs from './CalculatedColumnModal';
import RegNoTransactionsModal from './RegNoTransactionsModal';
import UnifiedDateTimeRangePicker from '../../common/UnifiedDateTimeRangePicker';
import FindBarJs from '../../common/FindBar';
import CopyableCellJs from '../AmendmentsAllocateTransactions/CopyableCell';
import ShareTransactionModalJs from '../AmendmentsAllocateTransactions/ShareTransactionModal';
import { useAuth as useAuthJs } from '../../../context/AuthContext';
import TabContextJs from '../../../context/TabContext';
import { useThemeTokens as useThemeTokensJs } from '../../../utils/themeTokens';
import { groupingScopeParams } from './routeSummaryParams';
import { useLoadStatuses } from '../../../hooks/useLoadStatuses';
import { loadStatusText } from '../../../services/loadStatus';
import { LoadStatusCell } from '../../common/LoadStatusBadge';
import type { GroupingId } from '../../../types/clientPages/routeSummary';
import type {
  CalculatedColumn,
  RangeFilter,
  ReconFilterOptions,
  ReconFilters,
  ReconFindMatch,
  ReconFindResponse,
  ReconTransaction,
  ReconViewResponse,
  RouteSummaryApiError,
} from '../../../types/clientPages/routeSummaryRecon';

// Typed views of the JS modules.
/** utils/formulaEvaluator.js: the calculated-column formula engine (a singleton). */
const formulaEvaluator = formulaEvaluatorJs as unknown as {
  evaluate: (
    formula: string | undefined,
    rowData: ReconTransaction,
    allRows?: ReconTransaction[] | null
  ) => unknown;
};
const CalculatedColumnModal = CalculatedColumnModalJs as unknown as ComponentType<{
  isOpen: boolean;
  onClose: () => void;
  onSave: (columns: CalculatedColumn[]) => void;
  existingColumns: CalculatedColumn[];
  availableColumns: ReconColumn[];
  currentGroupingId: GroupingId;
}>;
const FindBar = FindBarJs as unknown as ForwardRefExoticComponent<
  {
    value: string;
    onChange: (value: string) => void;
    onSubmit: (term: string) => void;
    onNext: () => void;
    onPrev: () => void;
    onClear: () => void;
    totalMatches: number | null;
    currentIndex: number;
    loading: boolean;
    capped: boolean;
    lastSearchedTerm: string | null;
    errorMessage: string | null;
    placeholder?: string;
  } & RefAttributes<HTMLInputElement>
>;
const CopyableCell = CopyableCellJs as unknown as ComponentType<{
  value: string | number | null | undefined;
  children?: ReactNode;
  className?: string;
}>;
/** ShareTransactionModal reads the Amendments' bracket-keyed field names. */
interface ShareTransactionData {
  'Ticket No': string | null;
  'Delivery No': string | null;
  'Reg No': string | null;
  'Ticket Timestamp': string;
  'Ticket Type': string | null;
  'Nett Weight': number | null;
  'Dispatch Location': string | null;
  'Receipt Location': string | null;
}
const ShareTransactionModal = ShareTransactionModalJs as unknown as ComponentType<{
  isOpen: boolean;
  onClose: () => void;
  transactionData: ShareTransactionData | null;
}>;
/** The slice of AuthContext the Recon view reads (DB-level app roles). */
const useAuth = (): { hasAnyAppRole: (roles: string[]) => boolean } =>
  useAuthJs() as unknown as { hasAnyAppRole: (roles: string[]) => boolean };
/** TabContext.js is untyped (createContext(null)); Recon only opens tabs. */
const TabContext = TabContextJs as unknown as Context<{
  openTab: (url: string, options?: { background?: boolean }) => void;
} | null>;
const useThemeTokens = useThemeTokensJs as unknown as () => { isDark: boolean };

/** A Recon table column: a base column or a calculated one. */
interface ReconColumn {
  key: string;
  label: string;
  type: string;
  isCalculated?: boolean;
  formula?: string;
  decimals?: number;
}

/** Per-vehicle identity chip colours. */
interface RegColor {
  bg: string;
  text: string;
}

export interface ReconViewProps {
  groupingId: GroupingId;
  groupingIds?: GroupingId[] | null;
  groupingName: string;
  startDate: string;
  endDate: string;
  minWeight: number;
  triggerLoad: number;
}

const MAX_AMENDMENT_SELECTION = 100;

// `groupingIds` (ST-00215, RSO-6): every route of a combined summary; Recon lists
// the tickets of all of them. `groupingId` is the first and still keys the saved
// column layout.
const ReconView = ({
  groupingId,
  groupingIds = null,
  groupingName,
  startDate,
  endDate,
  minWeight,
  triggerLoad,
}: ReconViewProps) => {
  const scopeKey = (groupingIds || []).join(',');
  const scopeParams = groupingScopeParams(groupingIds);
  const { isDark } = useThemeTokens();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ReconViewResponse | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(500); // Fixed at 500 as per requirements
  const abortControllerRef = useRef<AbortController | null>(null);

  // State to track if user has requested data load
  const [dataLoadRequested, setDataLoadRequested] = useState(false);

  // Column filters state - all filters except nettWeight and ticketTimestamp are arrays for multi-select
  // nettWeight and ticketTimestamp use range filters with from/to values
  const [filters, setFilters] = useState<ReconFilters>({
    date: [],
    ticketTimestamp: { from: '', to: '' },
    ticketType: [],
    ticketNo: [],
    deliveryNo: [],
    vehicleReg: [],
    dispatchLocation: [],
    receiptLocation: [],
    nettWeight: { from: '', to: '' },
    product: [],
  });

  // Applied filters - only updated when user clicks "Apply Filters"
  const [appliedFilters, setAppliedFilters] = useState<ReconFilters>({
    date: [],
    ticketTimestamp: { from: '', to: '' },
    ticketType: [],
    ticketNo: [],
    deliveryNo: [],
    vehicleReg: [],
    dispatchLocation: [],
    receiptLocation: [],
    nettWeight: { from: '', to: '' },
    product: [],
  });

  // Search terms for multi-select dropdowns
  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({
    date: '',
    ticketType: '',
    ticketNo: '',
    deliveryNo: '',
    vehicleReg: '',
    dispatchLocation: '',
    receiptLocation: '',
    product: '',
  });

  // Dropdown open state for multi-select
  const [dropdownOpen, setDropdownOpen] = useState<Record<string, boolean>>({
    date: false,
    ticketType: false,
    ticketNo: false,
    deliveryNo: false,
    vehicleReg: false,
    dispatchLocation: false,
    receiptLocation: false,
    product: false,
  });

  // Filter options for dropdowns
  const [filterOptions, setFilterOptions] = useState<ReconFilterOptions>({
    date: [],
    ticketType: [],
    ticketNo: [],
    deliveryNo: [],
    vehicleReg: [],
    dispatchLocation: [],
    receiptLocation: [],
    product: [],
  });
  const [loadingFilterOptions, setLoadingFilterOptions] = useState(false);

  // Filter collapse state (default to collapsed)
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(true);

  // Calculated columns state
  const [calculatedColumns, setCalculatedColumns] = useState<CalculatedColumn[]>([]);
  const [showCalculatedColumnModal, setShowCalculatedColumnModal] = useState(false);

  // Column visibility state
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set());
  const [showColumnToggle, setShowColumnToggle] = useState(false);

  // Share-transaction modal state — holds a row reshaped for ShareTransactionModal
  const [shareTransactionData, setShareTransactionData] = useState<ShareTransactionData | null>(
    null
  );

  // Reg No modal state
  const [showRegNoModal, setShowRegNoModal] = useState(false);
  const [selectedRegNo, setSelectedRegNo] = useState<string | null>(null);
  const [clickedTicketNo, setClickedTicketNo] = useState<string | null>(null);
  // Rows the user has manually click-selected. Distinct from clickedTicketNo
  // (which drives find/RegNo scroll-into-view) so manual clicks don't snap scroll.
  const [selectedTicketNos, setSelectedTicketNos] = useState<Set<string>>(() => new Set());

  // --- Admin-only: bulk-select rows to open in Amendments ---
  // Gate on DB-level roles (via /api/tenants/current → AuthContext.appRoles), NOT
  // Keycloak realm roles — the app's admin grants live in the UserRoles table.
  const { hasAnyAppRole } = useAuth();
  // null when the tab strip is off — see openSelectedInAmendments.
  const tabs = useContext(TabContext);
  const canBulkOpenAmendments = hasAnyAppRole(['global-admin', 'administrator']);
  const [amendSelectMode, setAmendSelectMode] = useState(false);
  const [amendSelectedUIDs, setAmendSelectedUIDs] = useState<Set<string>>(() => new Set());

  // --- Find-in-results state (mirrors amendments) ---
  const [findTerm, setFindTerm] = useState('');
  const [findMatches, setFindMatches] = useState<ReconFindMatch[] | null>(null);
  const [findIndex, setFindIndex] = useState(0);
  const [findLoading, setFindLoading] = useState(false);
  const [findError, setFindError] = useState<string | null>(null);
  const [findCapped, setFindCapped] = useState(false);
  const [lastSearchedTerm, setLastSearchedTerm] = useState<string | null>(null);
  const findInputRef = useRef<HTMLInputElement>(null);
  const findAbortRef = useRef<AbortController | null>(null);
  const rowRefs = useRef<Record<string, HTMLTableRowElement>>({});

  // Measure header height so the filter bar can stick just below it (header
  // height varies when select-mode banner is shown)
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setHeaderHeight(entry.contentRect.height);
      }
    });
    observer.observe(el);
    setHeaderHeight(el.offsetHeight);
    return () => observer.disconnect();
  }, []);

  // Nett weight selection state for sum tooltip
  const [selectedWeightIndices, setSelectedWeightIndices] = useState<Set<number>>(new Set());

  // Toggle weight cell selection
  const handleWeightCellClick = useCallback((e: MouseEvent, index: number) => {
    e.stopPropagation();
    setSelectedWeightIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  // Clear weight selection
  const clearWeightSelection = useCallback(() => {
    setSelectedWeightIndices(new Set());
  }, []);

  // --- Bulk amend selection handlers ---
  const toggleAmendSelectMode = useCallback(() => {
    setAmendSelectMode((prev) => {
      if (prev) {
        // Leaving selection mode — drop any selections
        setAmendSelectedUIDs(new Set());
      }
      return !prev;
    });
  }, []);

  const toggleAmendSelectedUID = useCallback((ticketUID: string | null | undefined) => {
    if (!ticketUID) return;
    setAmendSelectedUIDs((prev) => {
      const next = new Set(prev);
      if (next.has(ticketUID)) {
        next.delete(ticketUID);
      } else {
        if (next.size >= MAX_AMENDMENT_SELECTION) return prev;
        next.add(ticketUID);
      }
      return next;
    });
  }, []);

  const openSelectedInAmendments = useCallback(() => {
    if (amendSelectedUIDs.size === 0) return;
    const uids = Array.from(amendSelectedUIDs).slice(0, MAX_AMENDMENT_SELECTION);
    const qs = new URLSearchParams({ ticketUIDs: uids.join(',') }).toString();
    const url = `/amendments-allocate-transactions?${qs}`;
    // With the tab strip on this stays in the window, so Recon is still sitting
    // there when you come back. Without it, the old new-window behaviour.
    if (tabs) tabs.openTab(url);
    else window.open(url, '_blank', 'noopener');
    // Exit selection mode after launching so returning to Recon is clean
    setAmendSelectMode(false);
    setAmendSelectedUIDs(new Set());
  }, [amendSelectedUIDs, tabs]);

  // Load calculated columns from localStorage (global storage, filtered by route)
  useEffect(() => {
    const savedColumns = localStorage.getItem('calculatedColumns_global');
    if (savedColumns) {
      try {
        const allColumns = JSON.parse(savedColumns) as CalculatedColumn[];
        // Filter to only show columns that apply to this route
        const routeColumns = allColumns.filter(
          (col) => col.appliedRoutes && col.appliedRoutes.includes(groupingId)
        );
        setCalculatedColumns(routeColumns);
      } catch (e) {
        console.error('Error loading calculated columns:', e);
        setCalculatedColumns([]);
      }
    } else {
      // Migration: Check for old per-route storage
      const oldSavedColumns = localStorage.getItem(`calculatedColumns_${groupingId}`);
      if (oldSavedColumns) {
        try {
          const oldColumns = JSON.parse(oldSavedColumns) as CalculatedColumn[];
          // Migrate old columns to new format with appliedRoutes
          const migratedColumns = oldColumns.map((col) => ({
            ...col,
            appliedRoutes: col.appliedRoutes || [groupingId],
          }));
          setCalculatedColumns(migratedColumns);
          // Save in new format
          localStorage.setItem('calculatedColumns_global', JSON.stringify(migratedColumns));
          // Remove old storage
          localStorage.removeItem(`calculatedColumns_${groupingId}`);
        } catch (e) {
          console.error('Error migrating calculated columns:', e);
          setCalculatedColumns([]);
        }
      }
    }
  }, [groupingId]);

  // Load hidden columns from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(`hiddenColumns_${groupingId}`);
    if (saved) {
      try {
        setHiddenColumns(new Set(JSON.parse(saved) as string[]));
      } catch (e) {
        setHiddenColumns(new Set());
      }
    } else {
      setHiddenColumns(new Set());
    }
  }, [groupingId]);

  // Save calculated columns to localStorage (global storage)
  const handleSaveCalculatedColumns = (columns: CalculatedColumn[]) => {
    // Save all columns globally
    localStorage.setItem('calculatedColumns_global', JSON.stringify(columns));

    // Filter to show only columns for current route
    const routeColumns = columns.filter(
      (col) => col.appliedRoutes && col.appliedRoutes.includes(groupingId)
    );
    setCalculatedColumns(routeColumns);
  };

  // Column visibility toggle handlers
  const toggleColumnVisibility = (columnKey: string) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(columnKey)) {
        next.delete(columnKey);
      } else {
        next.add(columnKey);
      }
      localStorage.setItem(`hiddenColumns_${groupingId}`, JSON.stringify([...next]));
      return next;
    });
  };

  const showAllColumns = () => {
    setHiddenColumns(new Set());
    localStorage.removeItem(`hiddenColumns_${groupingId}`);
  };

  // Define base columns in the EXACT order specified in requirements
  const baseColumns: ReconColumn[] = [
    { key: 'date', label: 'Date', type: 'date' },
    { key: 'ticketTimestamp', label: 'Ticket Timestamp', type: 'datetime' },
    { key: 'ticketType', label: 'Ticket Type', type: 'text' },
    { key: 'ticketNo', label: 'Ticket No', type: 'text' },
    { key: 'deliveryNo', label: 'Delivery No', type: 'text' },
    { key: 'vehicleReg', label: 'Reg No', type: 'text' },
    { key: 'dispatchLocation', label: 'Dispatch Location', type: 'text' },
    { key: 'receiptLocation', label: 'Receipt Location', type: 'text' },
    { key: 'nettWeight', label: 'Nett Weight', type: 'number' },
    { key: 'product', label: 'Product', type: 'text' },
  ];

  // ST-00218: the Load status labels of the loaded tickets; the column exists only
  // when the server answers that load matching is on.
  const loadStatusUids = useMemo(() => (data?.transactions || []).map((t) => t.ticketUID), [data]);
  const {
    enabled: loadStatusEnabled,
    statuses: loadStatuses,
    loading: loadStatusLoading,
  } = useLoadStatuses(loadStatusUids);
  const showLoadStatus = loadStatusEnabled === true;
  const loadStatusOf = (transaction: ReconTransaction) =>
    transaction.ticketUID ? loadStatuses[transaction.ticketUID] : null;

  // Combine base columns (+ Load status after Ticket No) with calculated columns
  const columns: ReconColumn[] = [
    ...baseColumns.flatMap((col) =>
      col.key === 'ticketNo' && showLoadStatus
        ? [col, { key: 'loadStatus', label: 'Load status', type: 'status' }]
        : [col]
    ),
    ...calculatedColumns.map((calc) => ({
      key: calc.key,
      label: calc.label,
      type: calc.type,
      isCalculated: true,
      formula: calc.formula,
      decimals: calc.decimals,
    })),
  ];

  // Filter to only visible columns
  const visibleColumns = columns.filter((c) => !hiddenColumns.has(c.key));
  // Hidden columns that exist right now (a hidden Load status stays in the saved
  // set while load matching is off).
  const hiddenCount = columns.length - visibleColumns.length;

  const fetchFilterOptions = useCallback(async () => {
    if (!groupingId || !startDate || !endDate) {
      return;
    }

    setLoadingFilterOptions(true);

    try {
      console.log('Fetching filter options for recon view...');

      const response = await api.routeSummary.getReconViewFilterOptions(groupingId, {
        startDate,
        endDate,
        minWeight,
        ...scopeParams,
      });

      console.log('Filter options response:', response.data);
      setFilterOptions(response.data as ReconFilterOptions);
    } catch (err) {
      console.error('Error fetching filter options:', err);
      // Don't show error to user, just log it - filters will remain as text inputs
    } finally {
      setLoadingFilterOptions(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scopeParams is derived from scopeKey
  }, [groupingId, scopeKey, startDate, endDate, minWeight]); // baseColumns not needed as dependency

  const fetchTransactions = useCallback(
    async (page: number = 1) => {
      // Cancel previous request if any
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      setLoading(true);
      setError(null);

      try {
        // Check if any calculated column filters are active
        // Base columns are the standard database columns (defined inline to avoid dependency)
        const baseColumnKeys = [
          'date',
          'ticketTimestamp',
          'ticketType',
          'ticketNo',
          'deliveryNo',
          'vehicleReg',
          'dispatchLocation',
          'receiptLocation',
          'nettWeight',
          'product',
        ];
        const hasCalculatedColumnFilters = Object.keys(filters).some((key) => {
          // If the filter key is not in base columns and has a value, it's a calculated column filter
          return (
            !baseColumnKeys.includes(key) &&
            filters[key] &&
            (Array.isArray(filters[key]) ? filters[key].length > 0 : filters[key] !== '')
          );
        });

        // If calculated column filters are active, fetch ALL data (no pagination)
        // Otherwise, use normal pagination
        const fetchLimit = hasCalculatedColumnFilters ? 999999 : pageSize;
        const fetchPage = hasCalculatedColumnFilters ? 1 : page;

        // CRITICAL FIX: When calculated column filters are active, we need to send ONLY non-ticketType base filters
        // to the server. This ensures ALL ticket types are fetched (needed for LOOKUP formulas),
        // while still applying other filters like date, location, etc.
        const serverFilters = hasCalculatedColumnFilters
          ? { ...filters, ticketType: '' } // Clear ticketType filter for server
          : filters;

        console.log('[ReconView] fetchTransactions: Fetching with params:', {
          groupingId,
          startDate,
          endDate,
          minWeight,
          page: fetchPage,
          limit: fetchLimit,
          filters,
          serverFilters,
          baseColumnKeys,
          hasCalculatedColumnFilters,
          note: hasCalculatedColumnFilters
            ? 'Fetching ALL data for calculated column filtering (ticketType filter will be applied client-side)'
            : 'Normal pagination',
        });

        const response = await api.routeSummary.getReconViewTransactions(
          groupingId,
          {
            startDate,
            endDate,
            minWeight,
            page: fetchPage,
            limit: fetchLimit,
            filters: JSON.stringify(serverFilters),
            ...scopeParams,
          },
          { signal: abortControllerRef.current.signal }
        );

        console.log('[ReconView] fetchTransactions: Response received:', {
          transactionCount: response.data?.transactions?.length,
          totalRecords: response.data?.pagination?.totalRecords,
          currentPage: response.data?.pagination?.currentPage,
          totalPages: response.data?.pagination?.totalPages,
          hasCalculatedColumnFilters,
        });
        setData(response.data as ReconViewResponse);
        setCurrentPage(page);
      } catch (caught) {
        const err = caught as RouteSummaryApiError;
        if (err.name !== 'AbortError') {
          console.error('Error fetching recon view transactions:', err);

          let errorMessage = 'Failed to load transactions';
          if (err.response?.data?.message) {
            errorMessage = err.response.data.message;
          } else if (err.response?.data?.error) {
            errorMessage = err.response.data.error;
          } else if (err.message) {
            errorMessage = err.message;
          }

          setError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scopeParams is derived from scopeKey
    [groupingId, scopeKey, startDate, endDate, minWeight, pageSize, filters]
  );

  // Reset data load state when route changes
  useEffect(() => {
    setDataLoadRequested(false);
    setData(null);
    setError(null);
    setCurrentPage(1);
    // Clear filters when route changes
    setFilters({
      date: [],
      ticketTimestamp: { from: '', to: '' },
      ticketType: [],
      ticketNo: [],
      deliveryNo: [],
      vehicleReg: [],
      dispatchLocation: [],
      receiptLocation: [],
      nettWeight: { from: '', to: '' },
      product: [],
    });
    setAppliedFilters({
      date: [],
      ticketTimestamp: { from: '', to: '' },
      ticketType: [],
      ticketNo: [],
      deliveryNo: [],
      vehicleReg: [],
      dispatchLocation: [],
      receiptLocation: [],
      nettWeight: { from: '', to: '' },
      product: [],
    });
  }, [groupingId, scopeKey]);

  // Auto-load data when triggerLoad changes (triggered by Generate Summary button)
  useEffect(() => {
    if (triggerLoad && groupingId && startDate && endDate && !dataLoadRequested) {
      handleGenerateReconView();
    }
  }, [triggerLoad]); // Only depend on triggerLoad to avoid infinite loops

  // Fetch filter options only when data is loaded and filters are expanded
  useEffect(() => {
    if (dataLoadRequested && !isFiltersCollapsed) {
      fetchFilterOptions();
    }
  }, [dataLoadRequested, isFiltersCollapsed, fetchFilterOptions]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: globalThis.MouseEvent) => {
      // Check if click is outside any dropdown
      const isOutside = !(event.target as Element).closest('.multi-select-dropdown');
      if (isOutside) {
        closeAllDropdowns();
      }
    };

    // Check if any dropdown is open
    const anyDropdownOpen = Object.values(dropdownOpen).some((isOpen) => isOpen);

    if (anyDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    return undefined;
  }, [dropdownOpen]);

  // Manual data load function - only called when user clicks "Generate Recon View"
  const handleGenerateReconView = async () => {
    if (!groupingId || !startDate || !endDate) {
      setError('Please ensure a route is selected with valid date range');
      return;
    }

    // Cancel previous request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);
    setDataLoadRequested(true);

    try {
      // Mirror fetchTransactions: when a calculated-column filter is active,
      // fetch ALL rows (server can't evaluate the formula) and clear ticketType
      // server-side so LOOKUPs still see all ticket types. Without this, Reload
      // with a custom-col filter applied would server-fetch 500 rows and the
      // client memo would trim them — leaving the table empty.
      const baseColumnKeys = [
        'date',
        'ticketTimestamp',
        'ticketType',
        'ticketNo',
        'deliveryNo',
        'vehicleReg',
        'dispatchLocation',
        'receiptLocation',
        'nettWeight',
        'product',
      ];
      const hasCalculatedColumnFilters = Object.keys(appliedFilters).some((key) => {
        return (
          !baseColumnKeys.includes(key) &&
          appliedFilters[key] &&
          (Array.isArray(appliedFilters[key])
            ? appliedFilters[key].length > 0
            : appliedFilters[key] !== '')
        );
      });
      const fetchLimit = hasCalculatedColumnFilters ? 999999 : pageSize;
      const serverFilters = hasCalculatedColumnFilters
        ? { ...appliedFilters, ticketType: '' }
        : appliedFilters;

      console.log('Fetching recon view transactions with:', {
        groupingId,
        startDate,
        endDate,
        minWeight,
        page: 1,
        limit: fetchLimit,
        hasCalculatedColumnFilters,
      });

      const response = await api.routeSummary.getReconViewTransactions(
        groupingId,
        {
          startDate,
          endDate,
          minWeight,
          page: 1,
          limit: fetchLimit,
          filters: JSON.stringify(serverFilters),
          ...scopeParams,
        },
        { signal: controller.signal }
      );

      console.log('Recon view transactions response:', response);
      setData(response.data as ReconViewResponse);
      setCurrentPage(1);

      // Fetch filter options after data is loaded
      fetchFilterOptions();
    } catch (caught) {
      const err = caught as RouteSummaryApiError;
      if (err.name !== 'AbortError' && !controller.signal.aborted) {
        console.error('Error fetching recon view transactions:', err);

        let errorMessage = 'Failed to load transactions';
        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.response?.data?.error) {
          errorMessage = err.response.data.error;
        } else if (err.message) {
          errorMessage = err.message;
        }

        setError(errorMessage);
        setDataLoadRequested(false);
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= (data?.pagination?.totalPages || 1)) {
      setSelectedWeightIndices(new Set()); // Clear weight selection on page change
      fetchTransactions(newPage);
    }
  };

  // --- Find-in-results handlers ---
  const clearFind = useCallback(() => {
    if (findAbortRef.current) {
      try {
        findAbortRef.current.abort();
      } catch (_) {}
      findAbortRef.current = null;
    }
    setFindTerm('');
    setFindMatches(null);
    setFindIndex(0);
    setFindError(null);
    setFindCapped(false);
    setLastSearchedTerm(null);
    setClickedTicketNo(null);
  }, []);

  const gotoMatch = useCallback(
    (index: number, matches: ReconFindMatch[] | null) => {
      if (!matches || matches.length === 0) return;
      const count = matches.length;
      const wrapped = ((index % count) + count) % count;
      setFindIndex(wrapped);
      // In range: wrapped is reduced modulo a non-zero length.
      const match = matches[wrapped] as ReconFindMatch;
      setClickedTicketNo(match.ticketNo);
      if (match.page && match.page !== currentPage) {
        setSelectedWeightIndices(new Set());
        fetchTransactions(match.page);
      } else {
        // Already on the right page — scroll the row into view after render
        setTimeout(() => {
          const el = rowRefs.current[match.ticketNo];
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 0);
      }
    },
    [currentPage]
  ); // fetchTransactions is defined in same scope, not depended on to avoid churn

  const runFind = useCallback(
    async (rawTerm: string) => {
      const term = (rawTerm || '').trim();
      if (!term) {
        clearFind();
        return;
      }
      if (!groupingId || !startDate || !endDate) {
        setFindError('Select a grouping and date range first');
        return;
      }

      if (findAbortRef.current) {
        try {
          findAbortRef.current.abort();
        } catch (_) {}
      }
      findAbortRef.current = new AbortController();

      setFindLoading(true);
      setFindError(null);
      try {
        const res = await api.routeSummary.findInReconView(
          groupingId,
          {
            searchTerm: term,
            startDate,
            endDate,
            minWeight,
            limit: pageSize,
            filters: JSON.stringify(filters || {}),
            ...scopeParams,
          },
          { signal: findAbortRef.current.signal }
        );
        const data = (res.data || {}) as ReconFindResponse;
        const matches = Array.isArray(data.matches) ? data.matches : [];
        setFindMatches(matches);
        setFindCapped(!!data.capped);
        setLastSearchedTerm(term);
        if (matches.length > 0) {
          gotoMatch(0, matches);
        } else {
          setClickedTicketNo(null);
          setFindIndex(0);
        }
      } catch (caught) {
        const err = caught as RouteSummaryApiError;
        if (err.name === 'CanceledError' || err.name === 'AbortError') return;
        console.error('Recon find error:', err);
        setFindError(err.response?.data?.message || err.message || 'Find failed');
        setFindMatches([]);
        setLastSearchedTerm(term);
      } finally {
        setFindLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scopeParams is derived from scopeKey
    [groupingId, scopeKey, startDate, endDate, minWeight, pageSize, filters, gotoMatch, clearFind]
  );

  const findNext = useCallback(() => {
    if (findMatches && findMatches.length > 0) gotoMatch(findIndex + 1, findMatches);
  }, [findMatches, findIndex, gotoMatch]);

  const findPrev = useCallback(() => {
    if (findMatches && findMatches.length > 0) gotoMatch(findIndex - 1, findMatches);
  }, [findMatches, findIndex, gotoMatch]);

  // Invalidate find results when any scope parameter changes — ordinals would be stale
  useEffect(() => {
    setFindMatches(null);
    setFindIndex(0);
    setFindError(null);
    setFindCapped(false);
    setLastSearchedTerm(null);
    // Also reset bulk-amend selection — scope change means "start over"
    setAmendSelectedUIDs(new Set());
    // Clear manual row selection too — rows may no longer be in view
    setSelectedTicketNos(new Set());
  }, [groupingId, scopeKey, startDate, endDate, minWeight, filters, pageSize]);

  // Scroll matched row into view after a page fetch lands
  useEffect(() => {
    if (!clickedTicketNo) return;
    const el = rowRefs.current[clickedTicketNo];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [clickedTicketNo, data]);

  // Ctrl/Cmd+F override — focus the find input when this view is active
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      const isFind =
        (e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && (e.key === 'f' || e.key === 'F');
      if (!isFind) return;
      e.preventDefault();
      const el = findInputRef.current;
      if (el) {
        el.focus();
        el.select();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  const handleFilterChange = (columnKey: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [columnKey]: value,
    }));
  };

  // Handler for range filter changes (from/to)
  const handleRangeFilterChange = (
    columnKey: string,
    rangeType: keyof RangeFilter,
    value: string
  ) => {
    setFilters((prev) => ({
      ...prev,
      [columnKey]: {
        ...(prev[columnKey] as RangeFilter),
        [rangeType]: value,
      },
    }));
  };

  const handleMultiSelectToggle = (columnKey: string, value: string) => {
    setFilters((prev) => {
      const currentValues = (prev[columnKey] as string[] | undefined) || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];
      return {
        ...prev,
        [columnKey]: newValues,
      };
    });
  };

  const handleSearchTermChange = (columnKey: string, value: string) => {
    setSearchTerms((prev) => ({
      ...prev,
      [columnKey]: value,
    }));
  };

  const toggleDropdown = (columnKey: string) => {
    setDropdownOpen((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  const closeAllDropdowns = () => {
    setDropdownOpen({
      date: false,
      ticketType: false,
      ticketNo: false,
      deliveryNo: false,
      vehicleReg: false,
      dispatchLocation: false,
      receiptLocation: false,
      product: false,
    });
  };

  const applyFilters = () => {
    console.log('[ReconView] applyFilters called with filters:', filters);
    console.log(
      '[ReconView] Current data before filter:',
      data?.transactions?.length,
      'transactions'
    );

    // Update applied filters to trigger filtering
    setAppliedFilters(filters);
    setCurrentPage(1);
    fetchTransactions(1);
  };

  const clearFilters = () => {
    const emptyFilters: ReconFilters = {
      date: [],
      ticketTimestamp: { from: '', to: '' },
      ticketType: [],
      ticketNo: [],
      deliveryNo: [],
      vehicleReg: [],
      dispatchLocation: [],
      receiptLocation: [],
      nettWeight: { from: '', to: '' },
      product: [],
    };

    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setSearchTerms({
      date: '',
      ticketType: '',
      ticketNo: '',
      deliveryNo: '',
      vehicleReg: '',
      dispatchLocation: '',
      receiptLocation: '',
      product: '',
    });
    closeAllDropdowns();
    setCurrentPage(1);

    // Manually fetch with empty filters
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    setLoading(true);
    setError(null);

    api.routeSummary
      .getReconViewTransactions(
        groupingId,
        {
          startDate,
          endDate,
          minWeight,
          page: 1,
          limit: pageSize,
          filters: JSON.stringify(emptyFilters),
          ...scopeParams,
        },
        { signal: abortControllerRef.current.signal }
      )
      .then((response) => {
        setData(response.data as ReconViewResponse);
        setCurrentPage(1);
      })
      .catch((err: RouteSummaryApiError) => {
        if (err.name !== 'AbortError') {
          console.error('Error fetching recon view transactions:', err);
          setError('Failed to load transactions');
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const exportToCSV = () => {
    if (!filteredTransactions || filteredTransactions.length === 0) return;

    const headers = visibleColumns.map((col) => col.label);

    const csvData = [
      headers.join(','),
      ...filteredTransactions.map((row) => {
        const rowData = visibleColumns.map((col) => {
          let value = '';
          if (col.isCalculated) {
            const result = evaluateCalculatedColumn(row, col);
            value = result === null || result === undefined ? '' : String(result);
          } else {
            switch (col.key) {
              case 'date':
                value = formatMiningDate(row.ticketTimestamp);
                break;
              case 'ticketTimestamp':
                value = formatMiningDateTime(row.ticketTimestamp);
                break;
              case 'nettWeight':
                value = row.nettWeight?.toFixed(2) || '0.00';
                break;
              case 'loadStatus':
                value = loadStatusText(loadStatusOf(row));
                break;
              default:
                value = (row[col.key as keyof ReconTransaction] as string | null | undefined) || '';
            }
          }
          const escaped = String(value).replace(/"/g, '""');
          return `"${escaped}"`;
        });
        return rowData.join(',');
      }),
    ].join('\n');

    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recon-view-${groupingName}-${startDate}-to-${endDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Helper function to evaluate calculated column value
  const evaluateCalculatedColumn = (
    transaction: ReconTransaction,
    column: ReconColumn
  ): unknown => {
    if (!column.isCalculated) return null;

    try {
      const result = formulaEvaluator.evaluate(
        column.formula,
        transaction,
        data?.transactions || []
      );
      return result;
    } catch (error) {
      console.error('Error evaluating formula:', error);
      return '#ERROR';
    }
  };

  // Handler for clicking on Reg No
  const handleRegNoClick = (regNo: string, ticketNo: string | null) => {
    setSelectedRegNo(regNo);
    setClickedTicketNo(ticketNo);
    setShowRegNoModal(true);
  };

  // Handler for closing Reg No modal - keep clickedTicketNo to highlight the row
  const handleCloseRegNoModal = () => {
    setShowRegNoModal(false);
    setSelectedRegNo(null);
  };

  // ShareTransactionModal expects bracket-keyed fields (matches the Amendments shape).
  // Recon rows use camelCase, so reshape on open.
  const handleShareTransaction = (transaction: ReconTransaction) => {
    setShareTransactionData({
      'Ticket No': transaction.ticketNo,
      'Delivery No': transaction.deliveryNo,
      'Reg No': transaction.vehicleReg,
      'Ticket Timestamp': transaction.ticketTimestamp,
      'Ticket Type': transaction.ticketType,
      'Nett Weight': transaction.nettWeight,
      'Dispatch Location': transaction.dispatchLocation,
      'Receipt Location': transaction.receiptLocation,
    });
  };

  // Memoized filtered transactions - only recomputes when data or applied filters change
  // Prevents re-evaluation on every keystroke/UI interaction
  const filteredTransactions = useMemo(() => {
    if (!data || !data.transactions) return [];

    // Check if any calculated column filters are active
    const calculatedColumnFilters: Record<string, ReconFilters[string]> = {};
    columns.forEach((col) => {
      if (col.isCalculated && appliedFilters[col.key]) {
        calculatedColumnFilters[col.key] = appliedFilters[col.key] as ReconFilters[string];
      }
    });

    // If no calculated column filters, return all transactions as-is
    if (Object.keys(calculatedColumnFilters).length === 0) {
      return data.transactions;
    }

    // When calculated column filters are active, apply client-side filtering
    return data.transactions.filter((transaction) => {
      // Apply ticketType filter client-side (was cleared for server to fetch all types for LOOKUP)
      if (Array.isArray(appliedFilters.ticketType) && appliedFilters.ticketType.length > 0) {
        if (!appliedFilters.ticketType.includes(transaction.ticketType as string)) {
          return false;
        }
      } else if (
        appliedFilters.ticketType &&
        typeof appliedFilters.ticketType === 'string' &&
        appliedFilters.ticketType !== ''
      ) {
        if (transaction.ticketType !== appliedFilters.ticketType) {
          return false;
        }
      }

      // Apply calculated column filters
      for (const [columnKey, filterValue] of Object.entries(calculatedColumnFilters)) {
        if (!filterValue) continue;
        const column = columns.find((col) => col.key === columnKey);
        if (!column) continue;
        const calculatedValue = evaluateCalculatedColumn(transaction, column);
        if (!String(calculatedValue).toLowerCase().includes(String(filterValue).toLowerCase())) {
          return false;
        }
      }
      return true;
    });
  }, [data, appliedFilters, columns]);

  const regNoColorMap = useMemo(() => {
    const map = new Map<string, RegColor>();
    // Per-vehicle identity chips. Same ten hues in both themes: light gets a pale
    // tint + deep text, dark gets a deep tint + light text (ST-00127).
    const palette: RegColor[] = isDark
      ? [
          { bg: '#0C2A3F', text: '#7DD3FC' }, // sky
          { bg: '#3A2A0A', text: '#FCD34D' }, // amber
          { bg: '#0B2E22', text: '#6EE7B7' }, // emerald
          { bg: '#3A162B', text: '#F9A8D4' }, // pink
          { bg: '#2A1E4A', text: '#C4B5FD' }, // violet
          { bg: '#3A2013', text: '#FDBA74' }, // orange
          { bg: '#0B302C', text: '#5EEAD4' }, // teal
          { bg: '#3A1717', text: '#FCA5A5' }, // red
          { bg: '#1E2150', text: '#A5B4FC' }, // indigo
          { bg: '#33290A', text: '#FDE047' }, // yellow
        ]
      : [
          { bg: '#E0F2FE', text: '#0C4A6E' }, // sky
          { bg: '#FEF3C7', text: '#78350F' }, // amber
          { bg: '#D1FAE5', text: '#064E3B' }, // emerald
          { bg: '#FCE7F3', text: '#831843' }, // pink
          { bg: '#EDE9FE', text: '#4C1D95' }, // violet
          { bg: '#FFEDD5', text: '#7C2D12' }, // orange
          { bg: '#CCFBF1', text: '#134E4A' }, // teal
          { bg: '#FEE2E2', text: '#7F1D1D' }, // red
          { bg: '#E0E7FF', text: '#312E81' }, // indigo
          { bg: '#FEF9C3', text: '#713F12' }, // yellow
        ];
    let colorIdx = 0;
    for (const tx of filteredTransactions) {
      const reg = tx.vehicleReg;
      if (reg && !map.has(reg)) {
        map.set(reg, palette[colorIdx % palette.length] as RegColor);
        colorIdx++;
      }
    }
    return map;
  }, [filteredTransactions, isDark]);

  const renderCellContent = (transaction: ReconTransaction, column: ReconColumn) => {
    let content: ReactNode;

    // Handle calculated columns
    if (column.isCalculated) {
      const result = evaluateCalculatedColumn(transaction, column);

      if (result === '#ERROR') {
        return <span className="text-red-600 font-semibold">#ERROR</span>;
      }

      let displayText: string;
      if (column.type === 'number') {
        const decimals = column.decimals !== undefined ? column.decimals : 2;
        displayText =
          typeof result === 'number'
            ? result.toLocaleString(undefined, {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
              })
            : String(result);
      } else if (column.type === 'boolean') {
        displayText = result ? 'Yes' : 'No';
      } else {
        displayText = String(result);
      }

      return <CopyableCell value={displayText} />;
    }

    // Handle base columns
    switch (column.key) {
      case 'date':
        content = formatMiningDate(transaction.ticketTimestamp);
        break;
      case 'ticketTimestamp':
        return <CopyableCell value={formatMiningDateTime(transaction.ticketTimestamp)} />;
      case 'nettWeight':
        content =
          transaction.nettWeight?.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }) || '0.00';
        break;
      case 'ticketNo':
      case 'deliveryNo':
        return <CopyableCell value={transaction[column.key as 'ticketNo' | 'deliveryNo']} />;
      case 'loadStatus':
        return <LoadStatusCell status={loadStatusOf(transaction)} loading={loadStatusLoading} />;
      case 'vehicleReg': {
        const regNo = transaction.vehicleReg;
        if (!regNo) return <span className="text-slate-400">—</span>;
        const regColor = regNoColorMap.get(regNo);
        return (
          <CopyableCell value={regNo}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRegNoClick(regNo, transaction.ticketNo);
              }}
              className="hover:underline font-semibold transition-colors"
              style={regColor ? { color: regColor.text } : { color: 'var(--accent-text)' }}
              title={`View all transactions for ${regNo}`}
            >
              {regNo}
            </button>
          </CopyableCell>
        );
      }
      default:
        content = (transaction[column.key as keyof ReconTransaction] as ReactNode) || '-';
    }

    // Wrap content in a span to ensure it's selectable
    return <span style={{ userSelect: 'text', cursor: 'text' }}>{content}</span>;
  };

  if (!groupingId) {
    return (
      <div className="bg-surface rounded-xl shadow-lg p-8 text-center">
        <p className="text-gray-600 text-lg">Please select a grouping to view transactions</p>
      </div>
    );
  }

  // Show placeholder if data hasn't been requested yet
  if (!dataLoadRequested && !loading) {
    return (
      <div className="bg-surface rounded-xl shadow-xl overflow-visible border border-slate-200">
        {/* Header */}
        <div className="px-6 py-3 bg-gradient-to-r from-slate-800 to-slate-700 dark:from-slate-100 dark:to-slate-200 text-white">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold">Recon View - All Transactions</h2>
          </div>
        </div>

        {/* Waiting Message */}
        <div className="p-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="mb-6">
              <svg
                className="w-20 h-20 mx-auto text-slate-300 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">
                Waiting for Summary Generation
              </h3>
              <p className="text-slate-600 mb-1">
                Route: <span className="font-semibold text-slate-800">{groupingName}</span>
              </p>
              <p className="text-slate-600 mb-1">
                Date Range: <span className="font-semibold text-slate-800">{startDate}</span> to{' '}
                <span className="font-semibold text-slate-800">{endDate}</span>
              </p>
              <p className="text-slate-600 mb-4">
                Min Weight: <span className="font-semibold text-slate-800">{minWeight}t</span>
              </p>

              <p className="text-sm text-slate-500">
                Click "Generate Summary" to load both Summary and Recon View data
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface border border-slate-200 rounded-xl shadow-sm overflow-visible">
      {/* Header - Modern, clean design (sticky so it follows on scroll) */}
      <div
        ref={headerRef}
        className="sticky top-0 z-30 px-6 py-4 bg-surface/95 backdrop-blur-sm border-b border-slate-200 shadow-[0_1px_0_0_rgb(var(--shadow-rgb)/0.04)]"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-900">Recon View - All Transactions</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handleGenerateReconView}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-all duration-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              title="Reload transaction data"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              <span className="text-sm">Reload</span>
            </button>
            {/* Column Visibility Toggle */}
            <div className="relative">
              <button
                onClick={() => setShowColumnToggle(!showColumnToggle)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 text-sm font-medium ${
                  hiddenCount > 0
                    ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                title="Toggle column visibility"
              >
                {hiddenCount > 0 ? (
                  <FaEyeSlash className="text-sm" />
                ) : (
                  <FaEye className="text-sm" />
                )}
                <span className="text-sm">
                  {hiddenCount > 0 ? `${visibleColumns.length}/${columns.length}` : 'View'}
                </span>
              </button>
              {showColumnToggle && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setShowColumnToggle(false)} />
                  <div className="absolute right-0 top-full mt-1 z-40 bg-surface border border-slate-200 rounded-lg shadow-xl py-1 w-56 max-h-80 overflow-y-auto">
                    <div className="px-3 py-2 border-b border-slate-100 flex justify-between items-center">
                      <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                        Toggle Columns
                      </span>
                      {hiddenCount > 0 && (
                        <button
                          onClick={showAllColumns}
                          className="text-xs text-teal-600 hover:text-teal-800 font-medium"
                        >
                          Show All
                        </button>
                      )}
                    </div>
                    {columns.map((col) => (
                      <label
                        key={col.key}
                        className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={!hiddenColumns.has(col.key)}
                          onChange={() => toggleColumnVisibility(col.key)}
                          className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                        />
                        <span
                          className={`text-sm ${col.isCalculated ? 'text-purple-700 font-medium' : 'text-slate-700'}`}
                        >
                          {col.label}
                        </span>
                        {col.isCalculated && (
                          <FaCalculator className="text-purple-400 text-[10px]" />
                        )}
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
            <button
              onClick={() => setShowCalculatedColumnModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-all duration-200 text-sm font-medium"
            >
              <FaCalculator className="text-sm" />
              <span className="text-sm">Columns ({calculatedColumns.length})</span>
            </button>
            <button
              onClick={exportToCSV}
              disabled={!data || loading}
              className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-all duration-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaFileExport className="text-sm" />
              <span className="text-sm">Export CSV</span>
            </button>
            {canBulkOpenAmendments && (
              <>
                {!amendSelectMode ? (
                  <button
                    onClick={toggleAmendSelectMode}
                    disabled={!data || loading}
                    title="Select rows to open in Amendments"
                    className="flex items-center gap-2 px-4 py-2 bg-surface text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 transition-all duration-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FaCheckSquare className="text-sm text-primary-600" />
                    <span className="text-sm">Select to amend</span>
                  </button>
                ) : (
                  <>
                    <button
                      onClick={openSelectedInAmendments}
                      disabled={amendSelectedUIDs.size === 0}
                      title={
                        amendSelectedUIDs.size === 0
                          ? 'Tick at least one row first'
                          : `Open ${amendSelectedUIDs.size} ticket${amendSelectedUIDs.size === 1 ? '' : 's'} in Amendments (new tab)`
                      }
                      className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-all duration-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                    >
                      <FaExternalLinkAlt className="text-xs" />
                      <span className="text-sm tabular-nums">
                        Open {amendSelectedUIDs.size} in Amendments
                      </span>
                    </button>
                    <button
                      onClick={toggleAmendSelectMode}
                      className="flex items-center gap-2 px-3 py-2 bg-surface text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition-all duration-200 text-sm font-medium"
                      title="Cancel selection"
                    >
                      <FaTimes className="text-xs" />
                      <span className="text-sm">Cancel</span>
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
        {amendSelectMode && (
          <div className="mt-3 px-4 py-2 bg-primary-50 border border-primary-200 rounded-md text-xs text-primary-800 flex items-center gap-2">
            <FaCheckSquare className="flex-shrink-0" />
            <span>
              Selection mode — tick rows below (max {MAX_AMENDMENT_SELECTION}). Changing any filter,
              grouping or date range resets the selection.
            </span>
            {amendSelectedUIDs.size >= MAX_AMENDMENT_SELECTION && (
              <span className="ml-auto text-amber-700 font-medium">Limit reached</span>
            )}
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FaSpinner className="animate-spin w-8 h-8 text-primary-500 mx-auto mb-4" />
            <p className="text-gray-600">Loading transactions...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => fetchTransactions(currentPage)}
              className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Data Display */}
      {data && !loading && !error && (
        <>
          {/* Summary Stats - Clean, modern design */}
          <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {/* Dispatch Stats */}
              <div className="text-center p-2.5 bg-surface border border-slate-200 rounded-lg">
                <p className="text-[10px] font-bold text-slate-600 uppercase mb-1 tracking-wider">
                  Dispatch Count
                </p>
                <p className="text-base font-bold text-orange-600">
                  {(data.summary.dispatchCount || 0).toLocaleString()}
                </p>
              </div>
              <div className="text-center p-2.5 bg-surface border border-slate-200 rounded-lg">
                <p className="text-[10px] font-bold text-slate-600 uppercase mb-1 tracking-wider">
                  Dispatch Weight
                </p>
                <p className="text-base font-bold text-orange-600">
                  {(data.summary.dispatchWeight || 0).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
                <p className="text-[10px] text-slate-500 mt-0.5">tonnes</p>
              </div>

              {/* Receipt Stats */}
              <div className="text-center p-2.5 bg-surface border border-slate-200 rounded-lg">
                <p className="text-[10px] font-bold text-slate-600 uppercase mb-1 tracking-wider">
                  Receipt Count
                </p>
                <p className="text-base font-bold text-emerald-600">
                  {(data.summary.receiptCount || 0).toLocaleString()}
                </p>
              </div>
              <div className="text-center p-2.5 bg-surface border border-slate-200 rounded-lg">
                <p className="text-[10px] font-bold text-slate-600 uppercase mb-1 tracking-wider">
                  Receipt Weight
                </p>
                <p className="text-base font-bold text-emerald-600">
                  {(data.summary.receiptWeight || 0).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
                <p className="text-[10px] text-slate-500 mt-0.5">tonnes</p>
              </div>

              {/* Variance Stats */}
              <div
                className={`text-center p-2.5 rounded-lg border-2 ${
                  (data.summary.receiptWeight || 0) - (data.summary.dispatchWeight || 0) >= 0
                    ? 'bg-emerald-50 border-emerald-200'
                    : 'bg-red-50 border-red-200'
                }`}
              >
                <p className="text-[10px] font-bold text-slate-600 uppercase mb-1 tracking-wider">
                  Variance %
                </p>
                <p
                  className={`text-base font-bold ${
                    (data.summary.receiptWeight || 0) - (data.summary.dispatchWeight || 0) >= 0
                      ? 'text-emerald-700'
                      : 'text-red-700'
                  }`}
                >
                  {(() => {
                    const dispatchWeight = data.summary.dispatchWeight || 0;
                    const receiptWeight = data.summary.receiptWeight || 0;
                    const variance = receiptWeight - dispatchWeight;
                    const variancePercent =
                      dispatchWeight > 0 ? (variance / dispatchWeight) * 100 : 0;
                    return `${variancePercent >= 0 ? '+' : ''}${variancePercent.toFixed(2)}%`;
                  })()}
                </p>
              </div>

              {/* Unique Vehicles */}
              <div className="text-center p-2.5 bg-surface border border-slate-200 rounded-lg">
                <p className="text-[10px] font-bold text-slate-600 uppercase mb-1 tracking-wider">
                  Vehicles
                </p>
                <p className="text-base font-bold text-slate-900">
                  {(data.summary.uniqueVehicles || 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          {/* Filter Controls - Compact collapsible design; sticks directly under the header */}
          <div
            className="sticky z-20 border-b border-slate-200 bg-surface/95 backdrop-blur-sm"
            style={{ top: headerHeight }}
          >
            {isFiltersCollapsed ? (
              <div className="px-6 py-1.5 flex items-center gap-2">
                <button
                  onClick={() => setIsFiltersCollapsed(false)}
                  className="flex items-center gap-1.5 px-3 py-1 bg-slate-100 text-slate-600 rounded-md hover:bg-slate-200 transition-colors text-xs font-medium"
                >
                  <FaFilter className="w-2.5 h-2.5 flex-shrink-0" />
                  Filters
                  <FaChevronDown className="w-2.5 h-2.5 text-slate-400 flex-shrink-0" />
                </button>
              </div>
            ) : (
              <>
                <div className="px-6 py-2 flex justify-between items-center border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <FaFilter className="text-slate-600 w-3 h-3 flex-shrink-0" />
                    <span className="text-xs font-semibold text-slate-900 leading-none">
                      Column Filters
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={applyFilters}
                      className="px-3 py-1 text-xs font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                    >
                      Apply
                    </button>
                    <button
                      onClick={clearFilters}
                      className="px-3 py-1 text-xs font-medium bg-surface border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50 transition-colors flex items-center gap-1"
                    >
                      <FaTimes className="text-[9px]" />
                      Clear
                    </button>
                    <button
                      onClick={() => setIsFiltersCollapsed(true)}
                      className="px-2 py-1 text-slate-400 hover:text-slate-600 transition-colors"
                      title="Collapse filters"
                    >
                      <FaChevronUp className="text-xs" />
                    </button>
                  </div>
                </div>
                <div className="px-6 py-2">
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
                    {visibleColumns.map((column) => {
                      // Load status: no server-side filter exists for it.
                      if (column.key === 'loadStatus') return null;
                      const options = filterOptions[column.key] || [];
                      const hasOptions = options.length > 0;

                      // Range inputs for nettWeight (numeric from/to)
                      if (column.key === 'nettWeight') {
                        const rangeValue = filters[column.key] || { from: '', to: '' };
                        return (
                          <div key={column.key} className="space-y-1">
                            <input
                              type="number"
                              placeholder={`From ${column.label}...`}
                              value={rangeValue.from || ''}
                              onChange={(e) =>
                                handleRangeFilterChange(column.key, 'from', e.target.value)
                              }
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                              step="0.01"
                            />
                            <input
                              type="number"
                              placeholder={`To ${column.label}...`}
                              value={rangeValue.to || ''}
                              onChange={(e) =>
                                handleRangeFilterChange(column.key, 'to', e.target.value)
                              }
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                              step="0.01"
                            />
                          </div>
                        );
                      }

                      // Datetime range picker for ticketTimestamp — matches UnifiedDateRangePicker visual language;
                      // compact, as tall as the other column filters (ST-00312: forms get the 38 px field).
                      if (column.key === 'ticketTimestamp') {
                        const rangeValue = filters[column.key] || { from: '', to: '' };
                        return (
                          <div key={column.key}>
                            <UnifiedDateTimeRangePicker
                              size="compact"
                              startValue={rangeValue.from || ''}
                              endValue={rangeValue.to || ''}
                              onStartChange={(v) => handleRangeFilterChange(column.key, 'from', v)}
                              onEndChange={(v) => handleRangeFilterChange(column.key, 'to', v)}
                              placeholder={`All ${column.label}`}
                            />
                          </div>
                        );
                      }

                      // Multi-select with search and checkboxes for all other base columns
                      if (hasOptions && !column.isCalculated) {
                        const selectedValues = (filters[column.key] as string[] | undefined) || [];
                        const searchTerm = searchTerms[column.key] || '';
                        const isOpen = dropdownOpen[column.key];

                        // Filter options based on search term
                        const filteredOptions = options.filter((option) =>
                          option.toLowerCase().includes(searchTerm.toLowerCase())
                        );

                        return (
                          <div key={column.key} className="relative multi-select-dropdown">
                            <button
                              type="button"
                              onClick={() => toggleDropdown(column.key)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 bg-surface text-left flex items-center justify-between"
                              disabled={loadingFilterOptions}
                            >
                              <span className="truncate">
                                {selectedValues.length === 0
                                  ? `All ${column.label}`
                                  : `${selectedValues.length} selected`}
                              </span>
                              <FaChevronDown
                                className={`ml-1 text-[10px] transition-transform ${isOpen ? 'rotate-180' : ''}`}
                              />
                            </button>

                            {isOpen && (
                              <div className="absolute z-50 mt-1 w-full bg-surface border border-gray-300 rounded shadow-lg max-h-80 overflow-hidden">
                                {/* Search input */}
                                <div className="p-2 border-b border-gray-200 sticky top-0 bg-surface">
                                  <div className="relative">
                                    <FaSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs" />
                                    <input
                                      type="text"
                                      placeholder={`Search ${column.label}...`}
                                      value={searchTerm}
                                      onChange={(e) =>
                                        handleSearchTermChange(column.key, e.target.value)
                                      }
                                      className="w-full pl-7 pr-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </div>
                                </div>

                                {/* Options list */}
                                <div className="max-h-64 overflow-y-auto">
                                  {filteredOptions.length === 0 ? (
                                    <div className="px-3 py-2 text-sm text-gray-500">
                                      No matches found
                                    </div>
                                  ) : (
                                    filteredOptions.map((option, idx) => (
                                      <label
                                        key={idx}
                                        className="flex items-center px-3 py-2 hover:bg-teal-50 cursor-pointer text-sm"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={selectedValues.includes(option)}
                                          onChange={() =>
                                            handleMultiSelectToggle(column.key, option)
                                          }
                                          className="mr-2"
                                        />
                                        <span className="truncate">{option}</span>
                                      </label>
                                    ))
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      }

                      // Text input for calculated columns or columns without options
                      return (
                        <div key={column.key} className="relative">
                          <input
                            type="text"
                            placeholder={`Filter ${column.label}...`}
                            value={(filters[column.key] as string | undefined) || ''}
                            onChange={(e) => handleFilterChange(column.key, e.target.value)}
                            className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-primary-500 ${
                              column.isCalculated
                                ? 'border-primary-300 bg-primary-50 pr-7 focus:ring-primary-500'
                                : 'border-gray-300 focus:ring-primary-500'
                            }`}
                            title={
                              column.isCalculated ? 'Calculated column - filters all data' : ''
                            }
                          />
                          {column.isCalculated && (
                            <span className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
                              <FaCalculator className="text-purple-400 text-xs" />
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Pagination Info - Modern, clean design */}
          {data.pagination && (
            <div className="sticky top-0 z-10 px-6 py-3 bg-surface border-b border-slate-200 flex justify-between items-center shadow-sm">
              <div className="text-sm text-slate-700">
                {(() => {
                  const totalFiltered = filteredTransactions.length;
                  const startRow = totalFiltered > 0 ? 1 : 0;
                  const endRow = totalFiltered;

                  return (
                    <>
                      Showing <span className="font-bold text-slate-900">{startRow}</span> to{' '}
                      <span className="font-bold text-slate-900">{endRow}</span> of{' '}
                      <span className="font-bold text-slate-900">
                        {totalFiltered.toLocaleString()}
                      </span>{' '}
                      transactions
                    </>
                  );
                })()}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={!data.pagination.hasPreviousPage}
                  className="p-2 bg-surface border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <FaChevronLeft className="w-3.5 h-3.5 text-slate-600" />
                </button>
                <span className="text-sm text-slate-700 px-2">
                  Page <span className="font-bold text-slate-900">{currentPage}</span> of{' '}
                  <span className="font-bold text-slate-900">{data.pagination.totalPages}</span>
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!data.pagination.hasNextPage}
                  className="p-2 bg-surface border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <FaChevronRight className="w-3.5 h-3.5 text-slate-600" />
                </button>
              </div>
            </div>
          )}

          {/* Find-in-results bar */}
          <FindBar
            ref={findInputRef}
            value={findTerm}
            onChange={setFindTerm}
            onSubmit={runFind}
            onNext={findNext}
            onPrev={findPrev}
            onClear={clearFind}
            totalMatches={findMatches ? findMatches.length : null}
            currentIndex={findIndex}
            loading={findLoading}
            capped={findCapped}
            lastSearchedTerm={lastSearchedTerm}
            errorMessage={findError}
            placeholder="Find in results  —  Ticket No, Delivery No, Reg No"
          />

          {/* Transactions Table - Modern, clean design */}
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse" style={{ userSelect: 'text' }}>
              <thead
                className="bg-slate-900 dark:bg-slate-50 shadow-sm"
                style={{ height: '2.75rem' }}
              >
                <tr>
                  <th
                    className="px-4 py-3 text-xs font-bold text-white uppercase tracking-wider border-r border-slate-700 dark:border-slate-200 text-center"
                    style={{ whiteSpace: 'nowrap', width: '60px' }}
                  >
                    #
                  </th>
                  <th
                    className="px-2 py-3 text-xs font-bold text-white uppercase tracking-wider border-r border-slate-700 dark:border-slate-200 text-center"
                    style={{ whiteSpace: 'nowrap', width: '40px' }}
                    title="Share transaction as image"
                  >
                    <FaShareAlt className="inline-block text-primary-300" />
                  </th>
                  {amendSelectMode && (
                    <th
                      className="px-3 py-3 text-xs font-bold text-white uppercase tracking-wider border-r border-slate-700 dark:border-slate-200 text-center"
                      style={{ whiteSpace: 'nowrap', width: '44px' }}
                      title="Select for amendment"
                    >
                      <FaCheckSquare className="inline-block text-primary-300" />
                    </th>
                  )}
                  {visibleColumns.map((column) => (
                    <th
                      key={column.key}
                      className={`px-4 py-3 text-xs font-bold text-white uppercase tracking-wider border-r border-slate-700 dark:border-slate-200 last:border-r-0 ${
                        column.type === 'number' ? 'text-right' : 'text-left'
                      }`}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-surface">
                {filteredTransactions.map((transaction, index) => (
                  <tr
                    key={`${transaction.ticketNo}-${index}`}
                    ref={(el) => {
                      if (transaction.ticketNo) {
                        if (el) rowRefs.current[transaction.ticketNo] = el;
                        else delete rowRefs.current[transaction.ticketNo];
                      }
                    }}
                    className={`border-b border-slate-200 transition-colors cursor-pointer hover:bg-slate-100 ${
                      transaction.ticketNo &&
                      (selectedTicketNos.has(transaction.ticketNo) ||
                        transaction.ticketNo === clickedTicketNo)
                        ? 'bg-yellow-100'
                        : transaction.ticketType === 'Receipt'
                          ? 'bg-emerald-50'
                          : 'bg-surface'
                    }`}
                    style={{ height: '2rem' }}
                    onClick={() => {
                      // Skip toggle if the user is selecting text to copy
                      if (window.getSelection && (window.getSelection() as Selection).toString())
                        return;
                      if (!transaction.ticketNo) return;
                      // Dismiss the find/RegNo highlight so clicks don't leave a phantom
                      // yellow row that the user can't deselect. Clearing to null won't
                      // re-trigger scrollIntoView (the effect early-returns on null).
                      if (clickedTicketNo) setClickedTicketNo(null);
                      setSelectedTicketNos((prev) => {
                        const next = new Set(prev);
                        // ticketNo is set: the handler returned above otherwise.
                        const ticketNo = transaction.ticketNo as string;
                        if (next.has(ticketNo)) next.delete(ticketNo);
                        else next.add(ticketNo);
                        return next;
                      });
                    }}
                  >
                    <td
                      className="px-3 py-2 text-sm text-slate-600 border-r border-slate-200 text-center font-medium"
                      style={{
                        whiteSpace: 'nowrap',
                        userSelect: 'text',
                        WebkitUserSelect: 'text',
                        MozUserSelect: 'text',
                        msUserSelect: 'text',
                      }}
                    >
                      {(currentPage - 1) * pageSize + index + 1}
                    </td>
                    <td
                      className="px-2 py-2 text-sm border-r border-slate-200 text-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShareTransaction(transaction);
                        }}
                        className="text-slate-400 hover:text-primary-600 p-1 rounded hover:bg-primary-50 transition-colors"
                        title="Share transaction as image"
                        aria-label={`Share ticket ${transaction.ticketNo || ''}`}
                      >
                        <FaShareAlt className="w-3 h-3" />
                      </button>
                    </td>
                    {amendSelectMode && (
                      <td
                        className="px-3 py-2 text-sm border-r border-slate-200 text-center"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleAmendSelectedUID(transaction.ticketUID);
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={
                            !!transaction.ticketUID && amendSelectedUIDs.has(transaction.ticketUID)
                          }
                          disabled={
                            !transaction.ticketUID ||
                            (!amendSelectedUIDs.has(transaction.ticketUID) &&
                              amendSelectedUIDs.size >= MAX_AMENDMENT_SELECTION)
                          }
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleAmendSelectedUID(transaction.ticketUID);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="form-checkbox h-4 w-4 text-primary-600 border-slate-300 rounded focus:ring-2 focus:ring-primary-500/40 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                          title={
                            !transaction.ticketUID
                              ? 'This row has no TicketUID — cannot select'
                              : undefined
                          }
                        />
                      </td>
                    )}
                    {visibleColumns.map((column) => (
                      <td
                        key={column.key}
                        className={`px-3 py-2 text-sm border-r border-slate-200 last:border-r-0 ${
                          column.key === 'nettWeight'
                            ? `text-right font-semibold cursor-pointer select-none transition-colors duration-150 ${
                                selectedWeightIndices.has(index)
                                  ? 'bg-teal-100 text-teal-900'
                                  : 'text-slate-900 hover:bg-teal-50'
                              }`
                            : column.type === 'number'
                              ? 'text-right font-semibold text-slate-900'
                              : 'text-left text-slate-900'
                        }`}
                        style={{
                          whiteSpace: 'nowrap',
                          ...(column.key === 'vehicleReg' &&
                          transaction.vehicleReg &&
                          regNoColorMap.get(transaction.vehicleReg)
                            ? {
                                backgroundColor: (
                                  regNoColorMap.get(transaction.vehicleReg) as RegColor
                                ).bg,
                              }
                            : {}),
                          ...(column.key !== 'nettWeight'
                            ? {
                                userSelect: 'text',
                                WebkitUserSelect: 'text',
                                MozUserSelect: 'text',
                                msUserSelect: 'text',
                              }
                            : {}),
                        }}
                        onClick={
                          column.key === 'nettWeight'
                            ? (e) => handleWeightCellClick(e, index)
                            : undefined
                        }
                        title={
                          column.key === 'nettWeight'
                            ? 'Click to select for sum calculation'
                            : undefined
                        }
                      >
                        {renderCellContent(transaction, column)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Fixed-position sum calculator for nett weight selection */}
            {selectedWeightIndices.size > 0 && (
              <div
                className="fixed bottom-5 left-1/2 z-50"
                style={{
                  transform: 'translateX(-50%)',
                  animation: 'reconWeightCalcFadeIn 200ms ease-out',
                }}
              >
                <style>{`
                  @keyframes reconWeightCalcFadeIn {
                    from { opacity: 0; transform: translateX(-50%) translateY(12px); }
                    to { opacity: 1; transform: translateX(-50%) translateY(0); }
                  }
                `}</style>
                <div className="inline-flex items-center gap-3 bg-slate-900 dark:bg-slate-50 text-white pl-4 pr-2 py-2.5 rounded-xl shadow-2xl shadow-slate-900/40 dark:shadow-black/40 border border-slate-700 dark:border-slate-200">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400 text-xs font-medium">
                      {selectedWeightIndices.size} selected
                    </span>
                    <span className="text-slate-600">|</span>
                    <span className="text-xs text-slate-400">Sum:</span>
                    <span className="text-sm font-bold text-white tabular-nums">
                      {(() => {
                        const filtered = filteredTransactions;
                        let sum = 0;
                        selectedWeightIndices.forEach((idx) => {
                          const tx = filtered[idx];
                          if (tx?.nettWeight) sum += tx.nettWeight;
                        });
                        return sum.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        });
                      })()}
                    </span>
                    <span className="text-xs text-slate-400">t</span>
                  </div>
                  <button
                    onClick={clearWeightSelection}
                    className="ml-1 p-1.5 hover:bg-slate-700 rounded-lg transition-colors text-slate-400 hover:text-white"
                    title="Clear selection"
                  >
                    <FaTimes className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Bottom Pagination */}
          {data.pagination && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
              <div className="text-sm text-gray-700">
                {(() => {
                  const totalFiltered = filteredTransactions.length;
                  const startRow = totalFiltered > 0 ? 1 : 0;
                  const endRow = totalFiltered;

                  return (
                    <>
                      Showing <span className="font-semibold">{startRow}</span> to{' '}
                      <span className="font-semibold">{endRow}</span> of{' '}
                      <span className="font-semibold">{totalFiltered.toLocaleString()}</span>{' '}
                      transactions
                    </>
                  );
                })()}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={!data.pagination.hasPreviousPage}
                  className="px-3 py-1 bg-surface border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <FaChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-700">
                  Page <span className="font-semibold">{currentPage}</span> of{' '}
                  <span className="font-semibold">{data.pagination.totalPages}</span>
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!data.pagination.hasNextPage}
                  className="px-3 py-1 bg-surface border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <FaChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Calculated Column Modal */}
      <CalculatedColumnModal
        isOpen={showCalculatedColumnModal}
        onClose={() => setShowCalculatedColumnModal(false)}
        onSave={handleSaveCalculatedColumns}
        existingColumns={calculatedColumns}
        availableColumns={baseColumns}
        currentGroupingId={groupingId}
      />

      {/* Reg No Transactions Modal */}
      <RegNoTransactionsModal
        isOpen={showRegNoModal}
        onClose={handleCloseRegNoModal}
        regNo={selectedRegNo}
        groupingId={groupingId}
        groupingIds={groupingIds}
        startDate={startDate}
        endDate={endDate}
        minWeight={minWeight}
        clickedTicketNo={clickedTicketNo}
      />

      {/* Share Transaction Modal — copy row as branded PNG / PDF */}
      <ShareTransactionModal
        isOpen={!!shareTransactionData}
        onClose={() => setShareTransactionData(null)}
        transactionData={shareTransactionData}
      />
    </div>
  );
};

export default ReconView;
